import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Tabs, 
  Form, 
  Input, 
  Switch, 
  Button, 
  Row, 
  Col, 
  Space,
  Divider,
  message,
  Upload,
  Avatar,
  InputNumber,
  Table,
  Modal,
  Popconfirm,
  Tag,
  Skeleton
} from 'antd';
import { 
  SettingOutlined, 
  UserOutlined, 
  BellOutlined, 
  SecurityScanOutlined,
  UploadOutlined,
  DollarOutlined,
  EnvironmentOutlined,
  MailOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  MobileOutlined,
  AppleOutlined,
  AndroidOutlined,
  RocketOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/authService';
import { getImageUrl, uploadAvatar } from '../services/uploadService';
import { 
  settingsService, 
  DiagnosticConfig, 
  Location, 
  EmailTemplates, 
  EmailTemplate,
  AppVersionSettings 
} from '../services/settingsService';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;
const { TextArea } = Input;

const ParametresPage: React.FC = () => {
  const { currentUser, updateCurrentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [avatarUrl, setAvatarUrl] = useState<string>(currentUser?.profile_image || '');
  const [avatarTimestamp, setAvatarTimestamp] = useState<number>(Date.now());
  const [profileForm] = Form.useForm();
  const [notificationForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  // États de chargement des actions utilisateur
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // État pour les configurations système
  const [diagnosticConfig, setDiagnosticConfig] = useState<DiagnosticConfig | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplates | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  
  // État pour les modales
  const [locationModalVisible, setLocationModalVisible] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<{ key: string; template: EmailTemplate } | null>(null);
  const [sessionsModalVisible, setSessionsModalVisible] = useState(false);
  const [locationForm] = Form.useForm();
  const [templateForm] = Form.useForm();
  const [diagnosticForm] = Form.useForm();
  const [appVersionForm] = Form.useForm();

  // État pour les versions mobiles
  const [appVersionSettings, setAppVersionSettings] = useState<AppVersionSettings | null>(null);
  const [loadingAppVersion, setLoadingAppVersion] = useState(false);
  const [savingAppVersion, setSavingAppVersion] = useState(false);
  
  // Pré-remplir les notifications depuis le profil utilisateur
  useEffect(() => {
    if (currentUser?.preferences?.notifications) {
      notificationForm.setFieldsValue(currentUser.preferences.notifications);
    }
  }, [currentUser, notificationForm]);

  // Charger les configurations quand on change d'onglet
  useEffect(() => {
    if (activeTab === 'diagnostic' && !diagnosticConfig) {
      loadDiagnosticConfig();
    } else if (activeTab === 'locations' && locations.length === 0) {
      loadLocations();
    } else if (activeTab === 'email' && !emailTemplates) {
      loadEmailTemplates();
    } else if (activeTab === 'app-version' && !appVersionSettings) {
      loadAppVersionConfig();
    }
  }, [activeTab]);
  
  const loadDiagnosticConfig = async () => {
    try {
      setLoadingConfig(true);
      const config = await settingsService.getDiagnosticConfig();
      setDiagnosticConfig(config);
      diagnosticForm.setFieldsValue({
        default_fee: config.default_fee
      });
    } catch (error) {
      console.error('Erreur chargement config diagnostic:', error);
      message.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoadingConfig(false);
    }
  };
  
  const loadLocations = async () => {
    try {
      setLoadingConfig(true);
      const data = await settingsService.getLocations();
      setLocations(data);
    } catch (error) {
      console.error('Erreur chargement locations:', error);
      message.error('Erreur lors du chargement des zones');
    } finally {
      setLoadingConfig(false);
    }
  };
  
  const loadEmailTemplates = async () => {
    try {
      setLoadingConfig(true);
      const templates = await settingsService.getEmailTemplates();
      setEmailTemplates(templates);
    } catch (error) {
      console.error('Erreur chargement templates:', error);
      message.error('Erreur lors du chargement des templates');
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleSaveProfile = async (values: any) => {
    try {
      // Préparer les données pour l'API
      const profileData = {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        phone: values.phone,
        // Les champs company et position peuvent être ajoutés selon le modèle backend
      };

      // Sauvegarder via l'API
      await authService.updateProfile(profileData);
      
      // Mettre à jour le contexte avec les nouvelles données
      updateCurrentUser({
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email
      });

      message.success('Profil mis à jour avec succès');
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      message.error('Erreur lors de la mise à jour du profil');
    }
  };

  const handleSaveNotifications = async (values: any) => {
    try {
      setSavingNotifications(true);
      const currentPreferences = currentUser?.preferences || {};
      const updatedPreferences = {
        ...currentPreferences,
        notifications: values
      };

      await authService.updateProfile({ preferences: updatedPreferences });
      updateCurrentUser({ preferences: updatedPreferences });
      message.success('Préférences de notification enregistrées avec succès');
    } catch (error: any) {
      console.error('Erreur sauvegarde notifications:', error);
      const errMsg = error?.response?.data?.message || 'Erreur lors de la sauvegarde des notifications';
      message.error(errMsg);
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleChangePassword = async (values: any) => {
    try {
      setChangingPassword(true);
      await authService.changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });
      message.success('Mot de passe mis à jour avec succès');
      passwordForm.resetFields();
    } catch (error: any) {
      console.error('Erreur changement de mot de passe:', error);
      const errMsg = error?.response?.data?.message || error?.response?.data?.error || 'Erreur lors du changement de mot de passe';
      message.error(errMsg);
    } finally {
      setChangingPassword(false);
    }
  };

  // Fonction pour gérer l'upload d'avatar
  const handleAvatarChange = async (info: any) => {
    const file = info.file?.originFileObj || info.file;
    
    if (!file) {
      return;
    }
    
    // Validation du fichier
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('Vous ne pouvez uploader que des fichiers JPG/PNG!');
      return;
    }
    
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('L\'image doit faire moins de 2MB!');
      return;
    }

    try {
      // Upload de l'image via l'API
      message.loading({ content: 'Upload en cours...', key: 'upload' });
      
      const uploadResult = await uploadAvatar(file);
      console.log('✅ Upload réussi:', uploadResult);
      
      // Récupérer l'URL de l'image uploadée
      const imageUrl = uploadResult.url;
      setAvatarUrl(imageUrl);
      
      // Mettre à jour le profil avec l'URL de l'image
      await authService.updateProfile({ 
        profile_image: imageUrl 
      });
      
      // Mettre à jour l'utilisateur actuel dans le contexte
      updateCurrentUser({ profile_image: imageUrl });
      
      // Forcer le rechargement de l'image en mettant à jour le timestamp
      setAvatarTimestamp(Date.now());
      
      message.success({ content: 'Photo de profil mise à jour avec succès', key: 'upload' });
    } catch (error) {
      console.error('❌ Erreur lors de l\'upload:', error);
      message.error({ content: 'Erreur lors de l\'upload de l\'image', key: 'upload' });
      // Réinitialiser l'avatar en cas d'erreur
      setAvatarUrl(currentUser?.profile_image || '');
    }
  };

  // Props pour le composant Upload
  const uploadProps = {
    name: 'avatar',
    listType: 'picture' as const,
    className: 'avatar-uploader',
    showUploadList: false,
    beforeUpload: (file: File) => {
      // Traiter le fichier directement et empêcher l'upload automatique
      handleAvatarChange({ file });
      return false; // Empêche l'upload automatique
    },
  };

  // Référence pour l'input de fichier caché
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const loadAppVersionConfig = async () => {
    try {
      setLoadingAppVersion(true);
      const data = await settingsService.getAppVersionConfig();
      setAppVersionSettings(data);
      appVersionForm.setFieldsValue({
        ios_latest_version: data.ios.latest_version,
        ios_latest_build: data.ios.latest_build,
        ios_min_version: data.ios.min_version,
        ios_min_build: data.ios.min_build,
        ios_force_update: data.ios.force_update,
        ios_title: data.ios.title,
        ios_message: data.ios.message,
        ios_store_url: data.ios.store_url,

        android_latest_version: data.android.latest_version,
        android_latest_build: data.android.latest_build,
        android_min_version: data.android.min_version,
        android_min_build: data.android.min_build,
        android_force_update: data.android.force_update,
        android_title: data.android.title,
        android_message: data.android.message,
        android_store_url: data.android.store_url,
      });
    } catch (error) {
      console.error('Erreur chargement version mobile:', error);
      message.error('Erreur lors du chargement des versions mobiles');
    } finally {
      setLoadingAppVersion(false);
    }
  };

  const handleSaveAppVersion = async (values: any) => {
    try {
      setSavingAppVersion(true);
      const payload = {
        ios: {
          latest_version: (values.ios_latest_version || '').trim(),
          latest_build: parseInt(values.ios_latest_build, 10) || 1,
          min_version: (values.ios_min_version || '').trim(),
          min_build: parseInt(values.ios_min_build, 10) || 1,
          force_update: Boolean(values.ios_force_update),
          title: values.ios_title || 'Mise à jour disponible',
          message: values.ios_message || '',
          store_url: (values.ios_store_url || '').trim(),
        },
        android: {
          latest_version: (values.android_latest_version || '').trim(),
          latest_build: parseInt(values.android_latest_build, 10) || 1,
          min_version: (values.android_min_version || '').trim(),
          min_build: parseInt(values.android_min_build, 10) || 1,
          force_update: Boolean(values.android_force_update),
          title: values.android_title || 'Mise à jour disponible',
          message: values.android_message || '',
          store_url: (values.android_store_url || '').trim(),
        }
      };
      const updated = await settingsService.updateAppVersionConfig(payload);
      setAppVersionSettings(updated);
      message.success('Configuration des versions mobiles enregistrée avec succès !');
    } catch (error) {
      console.error('Erreur sauvegarde version mobile:', error);
      message.error('Erreur lors de la sauvegarde des versions mobiles');
    } finally {
      setSavingAppVersion(false);
    }
  };

  const handleQuickSet1120 = () => {
    appVersionForm.setFieldsValue({
      ios_latest_version: '1.1.20',
      ios_latest_build: 57,
      android_latest_version: '1.1.20',
      android_latest_build: 57,
    });
    message.info('Formulaire pré-rempli avec 1.1.20 (Build 57). Cliquez sur "Enregistrer les modifications" pour valider.');
  };

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <SettingOutlined style={{ marginRight: 8 }} />
            Paramètres
          </Title>
          <Paragraph>Personnalisez les paramètres de votre tableau de bord.</Paragraph>
        </Col>
      </Row>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane 
            tab={
              <span>
                <UserOutlined />
                Profil
              </span>
            } 
            key="profile"
          >
            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleSaveProfile}
              initialValues={{
                firstName: currentUser?.first_name || currentUser?.name?.split(' ')[0] || '',
                lastName: currentUser?.last_name || currentUser?.name?.split(' ')[1] || '',
                email: currentUser?.email || '',
                phone: currentUser?.phone || '',
                company: 'MCT Maintenance',
                position: 'Administrateur'
              }}
            >
              <Row gutter={24}>
                <Col xs={24} lg={8}>
                  <div style={{ textAlign: 'center', marginBottom: 24 }}>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <div
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          display: 'inline-block'
                        }}
                        onClick={handleAvatarClick}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        <Avatar 
                          size={100} 
                          src={avatarUrl ? `${getImageUrl(avatarUrl)}?t=${avatarTimestamp}` : (currentUser?.profile_image ? `${getImageUrl(currentUser.profile_image)}?t=${avatarTimestamp}` : undefined)}
                          icon={!avatarUrl && !currentUser?.profile_image && <UserOutlined />} 
                          style={{ 
                            marginBottom: 16, 
                            border: '3px solid #f0f0f0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleAvatarChange({ file });
                          }
                        }}
                      />
                    </div>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                      Cliquez sur l'avatar ou utilisez le bouton ci-dessous
                    </Text>
                    <Upload {...uploadProps}>
                      <Button icon={<UploadOutlined />}>Changer la photo</Button>
                    </Upload>
                  </div>
                </Col>

                <Col xs={24} lg={16}>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="firstName"
                        label="Prénom"
                        rules={[{ required: true, message: 'Veuillez saisir votre prénom' }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="lastName"
                        label="Nom"
                        rules={[{ required: true, message: 'Veuillez saisir votre nom' }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { required: true, message: 'Veuillez saisir votre email' },
                      { type: 'email', message: 'Format d\'email invalide' }
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="phone"
                        label="Téléphone"
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="company"
                        label="Entreprise"
                      >
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="position"
                    label="Poste"
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Mettre à jour le profil
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane 
            tab={
              <span>
                <BellOutlined />
                Notifications
              </span>
            } 
            key="notifications"
          >
            <Form
              form={notificationForm}
              layout="vertical"
              onFinish={handleSaveNotifications}
              initialValues={{
                emailNotifications: true,
                smsNotifications: false,
                pushNotifications: true,
                orderNotifications: true,
                maintenanceNotifications: true,
                complaintNotifications: true,
                systemNotifications: false
              }}
            >
              <Row gutter={24}>
                <Col xs={24} lg={12}>
                  <Title level={4}>Canaux de notification</Title>
                  
                  <Form.Item
                    name="emailNotifications"
                    label="Notifications par email"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Activé" unCheckedChildren="Désactivé" />
                  </Form.Item>

                  <Form.Item
                    name="smsNotifications"
                    label="Notifications par SMS"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Activé" unCheckedChildren="Désactivé" />
                  </Form.Item>

                  <Form.Item
                    name="pushNotifications"
                    label="Notifications push"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Activé" unCheckedChildren="Désactivé" />
                  </Form.Item>
                </Col>

                <Col xs={24} lg={12}>
                  <Title level={4}>Types de notifications</Title>

                  <Form.Item
                    name="orderNotifications"
                    label="Nouvelles commandes"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Activé" unCheckedChildren="Désactivé" />
                  </Form.Item>

                  <Form.Item
                    name="maintenanceNotifications"
                    label="Interventions de maintenance"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Activé" unCheckedChildren="Désactivé" />
                  </Form.Item>

                  <Form.Item
                    name="complaintNotifications"
                    label="Nouvelles réclamations"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Activé" unCheckedChildren="Désactivé" />
                  </Form.Item>

                  <Form.Item
                    name="systemNotifications"
                    label="Notifications système"
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Activé" unCheckedChildren="Désactivé" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={savingNotifications}
                  icon={<SaveOutlined />}
                >
                  Sauvegarder les préférences de notification
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane 
            tab={
              <span>
                <SecurityScanOutlined />
                Sécurité
              </span>
            } 
            key="security"
          >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={4}>Changer le mot de passe</Title>
                <Form 
                  form={passwordForm}
                  layout="vertical"
                  onFinish={handleChangePassword}
                >
                  <Row gutter={16}>
                    <Col xs={24} lg={8}>
                      <Form.Item
                        name="currentPassword"
                        label="Mot de passe actuel"
                        rules={[{ required: true, message: 'Veuillez saisir votre mot de passe actuel' }]}
                      >
                        <Input.Password placeholder="••••••••" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item
                        name="newPassword"
                        label="Nouveau mot de passe"
                        rules={[
                          { required: true, message: 'Veuillez saisir un nouveau mot de passe' },
                          { min: 6, message: 'Le mot de passe doit comporter au moins 6 caractères' }
                        ]}
                      >
                        <Input.Password placeholder="Minimum 6 caractères" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item
                        name="confirmPassword"
                        label="Confirmer le mot de passe"
                        dependencies={['newPassword']}
                        rules={[
                          { required: true, message: 'Veuillez confirmer le mot de passe' },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              if (!value || getFieldValue('newPassword') === value) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error('Les deux mots de passe ne correspondent pas'));
                            },
                          })
                        ]}
                      >
                        <Input.Password placeholder="Répéter le mot de passe" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item>
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      loading={changingPassword}
                      icon={<SaveOutlined />}
                    >
                      Changer le mot de passe
                    </Button>
                  </Form.Item>
                </Form>
              </div>

              <Divider />

              <div>
                <Title level={4}>Authentification à deux facteurs</Title>
                <Space direction="vertical">
                  <Paragraph>Ajoutez une couche de sécurité supplémentaire à votre compte.</Paragraph>
                  <Button 
                    type="default"
                    onClick={() => {
                      Modal.info({
                        title: 'Authentification à deux facteurs (2FA)',
                        content: (
                          <div>
                            <p>La sécurité renforcée par double facteur (SMS / Authenticator) est en cours de déploiement pour la prochaine version de sécurité.</p>
                            <p>Votre compte est actuellement sécurisé par hachage bcrypt 12 rounds et session JWT.</p>
                          </div>
                        ),
                        okText: 'Compris'
                      });
                    }}
                  >
                    Configurer l'authentification à deux facteurs
                  </Button>
                </Space>
              </div>

              <Divider />

              <div>
                <Title level={4}>Sessions actives</Title>
                <Paragraph type="secondary">Gérez les appareils connectés à votre compte.</Paragraph>
                <Button 
                  type="default"
                  onClick={() => setSessionsModalVisible(true)}
                >
                  Voir les sessions actives
                </Button>
              </div>
            </Space>
          </TabPane>

          {/* Onglet Configuration Diagnostic */}
          <TabPane 
            tab={
              <span>
                <DollarOutlined />
                Diagnostic
              </span>
            } 
            key="diagnostic"
          >
            {loadingConfig ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                  <Title level={4}>Frais de Diagnostic</Title>
                  <Paragraph type="secondary">
                    Configurez les frais de diagnostic par défaut et par zone géographique.
                  </Paragraph>
                </div>

                <Form
                  form={diagnosticForm}
                  layout="vertical"
                  onFinish={async (values) => {
                    try {
                      const defaultFee = typeof values.default_fee !== 'undefined' ? Number(values.default_fee) : 0;
                      await settingsService.updateDiagnosticConfig({
                        default_fee: defaultFee,
                        fees_by_location: diagnosticConfig?.fees_by_location || {}
                      });
                      message.success('Configuration des frais de diagnostic mise à jour');
                      loadDiagnosticConfig();
                    } catch (error) {
                      message.error('Erreur lors de la mise à jour');
                    }
                  }}
                >
                  <Row gutter={24}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="default_fee"
                        label="Frais de diagnostic par défaut (FCFA)"
                        rules={[{ required: true, message: 'Veuillez saisir le montant' }]}
                        extra="Saisissez 0 FCFA pour rendre le diagnostic entièrement gratuit par défaut."
                      >
                        <InputNumber
                          style={{ width: '100%' }}
                          min={0}
                          step={500}
                          formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                          parser={value => value!.replace(/\s/g, '') as any}
                          addonAfter="FCFA"
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  <Title level={5}>Frais par Zone Géographique</Title>
                  <Paragraph type="secondary">
                    Vous pouvez définir des frais personnalisés pour certaines zones. Si une zone n'est pas configurée, le tarif par défaut sera appliqué.
                  </Paragraph>

                  {diagnosticConfig?.fees_by_location && Object.keys(diagnosticConfig.fees_by_location).length > 0 ? (
                    <Table
                      dataSource={Object.entries(diagnosticConfig.fees_by_location).map(([location, fee]) => ({
                        key: location,
                        location,
                        fee
                      }))}
                      columns={[
                        { title: 'Zone', dataIndex: 'location', key: 'location' },
                        { 
                          title: 'Frais (FCFA)', 
                          dataIndex: 'fee', 
                          key: 'fee',
                          render: (fee: number) => `${fee.toLocaleString('fr-FR')} FCFA`
                        },
                        {
                          title: 'Actions',
                          key: 'actions',
                          render: (_, record) => (
                            <Popconfirm
                              title="Supprimer ce tarif personnalisé ?"
                              onConfirm={async () => {
                                const newFees = { ...diagnosticConfig.fees_by_location };
                                delete newFees[record.location];
                                await settingsService.updateDiagnosticConfig({ fees_by_location: newFees });
                                loadDiagnosticConfig();
                                message.success('Tarif supprimé');
                              }}
                            >
                              <Button danger size="small" icon={<DeleteOutlined />} />
                            </Popconfirm>
                          )
                        }
                      ]}
                      pagination={false}
                      size="small"
                    />
                  ) : (
                    <Text type="secondary">Aucun tarif personnalisé configuré. Le tarif par défaut sera appliqué partout.</Text>
                  )}

                  <Form.Item style={{ marginTop: 24 }}>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                      Sauvegarder la configuration
                    </Button>
                  </Form.Item>
                </Form>
              </Space>
            )}
          </TabPane>

          {/* Onglet Zones de Service */}
          <TabPane 
            tab={
              <span>
                <EnvironmentOutlined />
                Zones
              </span>
            } 
            key="locations"
          >
            {loadingConfig ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Title level={4}>Zones de Service</Title>
                    <Paragraph type="secondary">
                      Gérez les villes et zones où vous proposez vos services.
                    </Paragraph>
                  </div>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingLocation(null);
                      locationForm.resetFields();
                      setLocationModalVisible(true);
                    }}
                  >
                    Ajouter une zone
                  </Button>
                </div>

                <Table
                  dataSource={locations}
                  rowKey="code"
                  columns={[
                    { title: 'Nom', dataIndex: 'name', key: 'name' },
                    { title: 'Code', dataIndex: 'code', key: 'code' },
                    { 
                      title: 'Statut', 
                      dataIndex: 'active', 
                      key: 'active',
                      render: (active: boolean) => (
                        <Tag color={active ? 'green' : 'red'}>
                          {active ? 'Active' : 'Inactive'}
                        </Tag>
                      )
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      render: (_, record) => (
                        <Space>
                          <Button 
                            size="small" 
                            icon={<EditOutlined />}
                            onClick={() => {
                              setEditingLocation(record);
                              locationForm.setFieldsValue(record);
                              setLocationModalVisible(true);
                            }}
                          />
                          <Popconfirm
                            title="Supprimer cette zone ?"
                            onConfirm={async () => {
                              const newLocations = locations.filter(l => l.code !== record.code);
                              await settingsService.updateLocations(newLocations);
                              setLocations(newLocations);
                              message.success('Zone supprimée');
                            }}
                          >
                            <Button danger size="small" icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </Space>
                      )
                    }
                  ]}
                  pagination={false}
                />
              </Space>
            )}

            {/* Modal pour ajouter/modifier une zone */}
            <Modal
              title={editingLocation ? 'Modifier la zone' : 'Ajouter une zone'}
              open={locationModalVisible}
              onCancel={() => setLocationModalVisible(false)}
              onOk={async () => {
                try {
                  const values = await locationForm.validateFields();
                  let newLocations;
                  
                  if (editingLocation) {
                    newLocations = locations.map(l => 
                      l.code === editingLocation.code ? values : l
                    );
                  } else {
                    newLocations = [...locations, values];
                  }
                  
                  await settingsService.updateLocations(newLocations);
                  setLocations(newLocations);
                  setLocationModalVisible(false);
                  message.success(editingLocation ? 'Zone modifiée' : 'Zone ajoutée');
                } catch (error) {
                  message.error('Erreur lors de la sauvegarde');
                }
              }}
            >
              <Form form={locationForm} layout="vertical">
                <Form.Item
                  name="name"
                  label="Nom de la zone"
                  rules={[{ required: true, message: 'Nom requis' }]}
                >
                  <Input placeholder="Ex: Dakar" />
                </Form.Item>
                <Form.Item
                  name="code"
                  label="Code"
                  rules={[{ required: true, message: 'Code requis' }]}
                >
                  <Input placeholder="Ex: DKR" disabled={!!editingLocation} />
                </Form.Item>
                <Form.Item
                  name="active"
                  label="Zone active"
                  valuePropName="checked"
                  initialValue={true}
                >
                  <Switch checkedChildren="Oui" unCheckedChildren="Non" />
                </Form.Item>
              </Form>
            </Modal>
          </TabPane>

          {/* Onglet Templates Email */}
          <TabPane 
            tab={
              <span>
                <MailOutlined />
                Emails
              </span>
            } 
            key="email"
          >
            {loadingConfig ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <div>
                  <Title level={4}>Templates d'Email</Title>
                  <Paragraph type="secondary">
                    Personnalisez les emails envoyés automatiquement. Utilisez les variables entre {'{{'}...{'}}'} pour insérer des données dynamiques.
                  </Paragraph>
                </div>

                <Table
                  dataSource={emailTemplates ? Object.entries(emailTemplates).map(([key, template]) => ({
                    key,
                    name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                    subject: template.subject,
                    template
                  })) : []}
                  rowKey="key"
                  columns={[
                    { title: 'Template', dataIndex: 'name', key: 'name' },
                    { 
                      title: 'Sujet', 
                      dataIndex: 'subject', 
                      key: 'subject',
                      ellipsis: true,
                      width: 300
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      width: 100,
                      render: (_, record) => (
                        <Button 
                          size="small" 
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditingTemplate({ key: record.key, template: record.template });
                            templateForm.setFieldsValue({
                              subject: record.template.subject,
                              body: record.template.body
                            });
                            setTemplateModalVisible(true);
                          }}
                        >
                          Modifier
                        </Button>
                      )
                    }
                  ]}
                  pagination={false}
                />
              </Space>
            )}

            {/* Modal pour modifier un template */}
            <Modal
              title={`Modifier le template: ${editingTemplate?.key.replace(/_/g, ' ')}`}
              open={templateModalVisible}
              onCancel={() => setTemplateModalVisible(false)}
              width={700}
              onOk={async () => {
                try {
                  const values = await templateForm.validateFields();
                  if (editingTemplate) {
                    await settingsService.updateEmailTemplates({
                      [editingTemplate.key]: {
                        subject: values.subject,
                        body: values.body
                      }
                    });
                    loadEmailTemplates();
                    setTemplateModalVisible(false);
                    message.success('Template mis à jour');
                  }
                } catch (error) {
                  message.error('Erreur lors de la sauvegarde');
                }
              }}
            >
              <Form form={templateForm} layout="vertical">
                <Form.Item
                  name="subject"
                  label="Sujet de l'email"
                  rules={[{ required: true, message: 'Sujet requis' }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="body"
                  label="Corps de l'email"
                  rules={[{ required: true, message: 'Corps requis' }]}
                >
                  <TextArea rows={10} />
                </Form.Item>
                <Text type="secondary">
                  Variables disponibles: {'{{customer_name}}'}, {'{{intervention_title}}'}, {'{{scheduled_date}}'}, {'{{technician_name}}'}, {'{{amount}}'}, {'{{offer_name}}'}, {'{{days_remaining}}'}
                </Text>
              </Form>
            </Modal>
          </TabPane>

          {/* Onglet Versions Mobiles */}
          <TabPane 
            tab={
              <span>
                <MobileOutlined />
                Applications Mobiles
              </span>
            } 
            key="app-version"
          >
            {loadingAppVersion ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : (
              <Form
                form={appVersionForm}
                layout="vertical"
                onFinish={handleSaveAppVersion}
              >
                <div style={{ marginBottom: 24 }}>
                  <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col xs={24} md={16}>
                      <Title level={4} style={{ marginBottom: 4 }}>
                        Gestion des Versions de l'Application Mobile
                      </Title>
                      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                        Configurez la dernière version en ligne sur l'App Store et Google Play.
                        Dès qu'une nouvelle version est enregistrée, les utilisateurs recevront automatiquement une invite de mise à jour à l'ouverture de l'application.
                      </Paragraph>
                    </Col>
                    <Col xs={24} md={8} style={{ textAlign: 'right' }}>
                      <Space wrap>
                        <Button 
                          icon={<ReloadOutlined />} 
                          onClick={loadAppVersionConfig}
                        >
                          Actualiser
                        </Button>
                        <Button 
                          type="dashed" 
                          icon={<RocketOutlined />} 
                          onClick={handleQuickSet1120}
                        >
                          Pré-remplir 1.1.20 (Build 57)
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                </div>

                <Row gutter={[24, 24]}>
                  {/* Carte iOS */}
                  <Col xs={24} lg={12}>
                    <Card 
                      title={
                        <Space>
                          <AppleOutlined style={{ fontSize: '22px', color: '#111827' }} />
                          <span style={{ fontWeight: 600 }}>Apple iOS (App Store)</span>
                        </Space>
                      }
                      extra={
                        <Tag color="blue">
                          Active : {appVersionSettings?.ios.latest_version || '1.1.16'} (b{appVersionSettings?.ios.latest_build || 51})
                        </Tag>
                      }
                      style={{ height: '100%', borderRadius: 12, border: '1px solid #E2E8F0' }}
                    >
                      <Row gutter={16}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="ios_latest_version"
                            label="Dernière version publiée"
                            tooltip="Numéro de version SemVer (ex: 1.1.20)"
                            rules={[{ required: true, message: 'Version requise' }]}
                          >
                            <Input placeholder="1.1.20" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="ios_latest_build"
                            label="Numéro de build récent"
                            tooltip="Numéro de build CFBundleVersion (ex: 57)"
                            rules={[{ required: true, message: 'Build requis' }]}
                          >
                            <InputNumber style={{ width: '100%' }} min={1} placeholder="57" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="ios_min_version"
                            label="Version minimale tolérée"
                            tooltip="Les versions antérieures seront bloquées (mise à jour obligatoire)"
                            rules={[{ required: true, message: 'Version minimale requise' }]}
                          >
                            <Input placeholder="1.1.0" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="ios_min_build"
                            label="Build minimal toléré"
                            tooltip="Build sous lequel la mise à jour devient obligatoire"
                            rules={[{ required: true, message: 'Build minimal requis' }]}
                          >
                            <InputNumber style={{ width: '100%' }} min={1} placeholder="40" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item
                        name="ios_force_update"
                        label="Mise à jour obligatoire (bloquante)"
                        valuePropName="checked"
                        tooltip="Si activé, l'utilisateur ne pourra pas fermer la boîte de dialogue sans mettre à jour l'application"
                      >
                        <Switch checkedChildren="Obligatoire" unCheckedChildren="Recommandée" />
                      </Form.Item>

                      <Form.Item
                        name="ios_title"
                        label="Titre de la fenêtre"
                      >
                        <Input placeholder="Mise à jour disponible" />
                      </Form.Item>

                      <Form.Item
                        name="ios_message"
                        label="Message explicatif"
                      >
                        <TextArea rows={3} placeholder="Une nouvelle version de l'application est disponible..." />
                      </Form.Item>

                      <Form.Item
                        name="ios_store_url"
                        label="Lien App Store"
                        rules={[{ required: true, message: 'Lien Store requis' }]}
                      >
                        <Input placeholder="https://apps.apple.com/ci/app/smart-maintenance/id6760555562" />
                      </Form.Item>
                    </Card>
                  </Col>

                  {/* Carte Android */}
                  <Col xs={24} lg={12}>
                    <Card 
                      title={
                        <Space>
                          <AndroidOutlined style={{ fontSize: '22px', color: '#10B981' }} />
                          <span style={{ fontWeight: 600 }}>Google Android (Play Store)</span>
                        </Space>
                      }
                      extra={
                        <Tag color="green">
                          Active : {appVersionSettings?.android.latest_version || '1.1.16'} (b{appVersionSettings?.android.latest_build || 51})
                        </Tag>
                      }
                      style={{ height: '100%', borderRadius: 12, border: '1px solid #E2E8F0' }}
                    >
                      <Row gutter={16}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="android_latest_version"
                            label="Dernière version publiée"
                            tooltip="Numéro de version SemVer (ex: 1.1.20)"
                            rules={[{ required: true, message: 'Version requise' }]}
                          >
                            <Input placeholder="1.1.20" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="android_latest_build"
                            label="Numéro de build récent"
                            tooltip="VersionCode Android (ex: 57)"
                            rules={[{ required: true, message: 'Build requis' }]}
                          >
                            <InputNumber style={{ width: '100%' }} min={1} placeholder="57" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row gutter={16}>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="android_min_version"
                            label="Version minimale tolérée"
                            tooltip="Les versions antérieures seront bloquées (mise à jour obligatoire)"
                            rules={[{ required: true, message: 'Version minimale requise' }]}
                          >
                            <Input placeholder="1.1.0" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                          <Form.Item
                            name="android_min_build"
                            label="Build minimal toléré"
                            tooltip="Build sous lequel la mise à jour devient obligatoire"
                            rules={[{ required: true, message: 'Build minimal requis' }]}
                          >
                            <InputNumber style={{ width: '100%' }} min={1} placeholder="40" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item
                        name="android_force_update"
                        label="Mise à jour obligatoire (bloquante)"
                        valuePropName="checked"
                        tooltip="Si activé, l'utilisateur ne pourra pas fermer la boîte de dialogue sans mettre à jour l'application"
                      >
                        <Switch checkedChildren="Obligatoire" unCheckedChildren="Recommandée" />
                      </Form.Item>

                      <Form.Item
                        name="android_title"
                        label="Titre de la fenêtre"
                      >
                        <Input placeholder="Mise à jour disponible" />
                      </Form.Item>

                      <Form.Item
                        name="android_message"
                        label="Message explicatif"
                      >
                        <TextArea rows={3} placeholder="Une nouvelle version de l'application est disponible..." />
                      </Form.Item>

                      <Form.Item
                        name="android_store_url"
                        label="Lien Google Play Store"
                        rules={[{ required: true, message: 'Lien Store requis' }]}
                      >
                        <Input placeholder="https://play.google.com/store/apps/details?id=com.remples.mct_maintenance_mobile" />
                      </Form.Item>
                    </Card>
                  </Col>
                </Row>

                <div style={{ marginTop: 24, textAlign: 'right' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    size="large"
                    loading={savingAppVersion}
                    style={{ minWidth: 220, borderRadius: 8 }}
                  >
                    Enregistrer les versions mobiles
                  </Button>
                </div>
              </Form>
            )}
          </TabPane>
        </Tabs>
      </Card>

      {/* Modal des sessions actives */}
      <Modal
        title="Sessions Actives"
        open={sessionsModalVisible}
        onCancel={() => setSessionsModalVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setSessionsModalVisible(false)}>
            Fermer
          </Button>
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Paragraph type="secondary">
            Appareils actuellement connectés à ce compte administrateur.
          </Paragraph>
          <Card size="small" style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Text strong>Session Actuelle (Ce navigateur)</Text>
                <div style={{ fontSize: '12px', color: '#64748B' }}>
                  Navigateur Web • {navigator.userAgent.includes('Mac') ? 'macOS' : navigator.userAgent.includes('Windows') ? 'Windows' : 'Linux / Autre'}
                </div>
              </Col>
              <Col>
                <Tag color="green">Active</Tag>
              </Col>
            </Row>
          </Card>
        </Space>
      </Modal>
    </div>
  );
};

export default ParametresPage;